import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, Calendar, Printer, Package } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';

const STATUS_CONFIG = {
  submitted:     { label: 'Submitted',     color: '#6366f1', bg: '#eef2ff' },
  reviewing:     { label: 'Reviewing',     color: '#8b5cf6', bg: '#f5f3ff' },
  quoted:        { label: 'Quoted',        color: '#f59e0b', bg: '#fffbeb' },
  approved:      { label: 'Approved',      color: '#3b82f6', bg: '#eff6ff' },
  in_production: { label: 'In Production', color: '#ec4899', bg: '#fdf2f8' },
  quality_check: { label: 'QC',            color: '#8b5cf6', bg: '#f5f3ff' },
  ready:         { label: 'Ready',         color: '#10b981', bg: '#ecfdf5' },
  delivered:     { label: 'Delivered',      color: '#059669', bg: '#d1fae5' },
  invoiced:      { label: 'Invoiced',      color: '#f97316', bg: '#fff7ed' },
  paid:          { label: 'Paid',          color: '#22c55e', bg: '#f0fdf4' },
  cancelled:     { label: 'Cancelled',     color: '#ef4444', bg: '#fef2f2' },
};

const SERVICE_LABELS = {
  printing: 'Printing',
  scanning: 'Scanning',
  reproduction: 'Reproduction',
  film_processing: 'Film Processing',
  framing: 'Framing',
  other: 'Other',
};

const SERVICE_ICONS = {
  printing: '🖨', scanning: '📷', reproduction: '🎨',
  film_processing: '🎞', framing: '🖼', other: '✦',
};

const CuratorServiceOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { selectedOrganization } = useOutletContext() || {};
  const orgId = selectedOrganization?.organization_id;

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  const [editQuotedPrice, setEditQuotedPrice] = useState('');
  const [editFinalPrice, setEditFinalPrice] = useState('');

  useEffect(() => {
    if (!orderId || !orgId) return;
    const fetchOrder = async () => {
      setLoading(true);
      const { data, error } = await supabase.from('service_orders')
        .select('*')
        .eq('id', orderId)
        .eq('organization_id', orgId)
        .single();
      if (error) {
        console.error('Failed to fetch order:', error);
        navigate('/curator/service-orders');
        return;
      }
      setOrder(data);
      setEditNotes(data.internal_notes || '');
      setEditQuotedPrice(data.quoted_price || '');
      setEditFinalPrice(data.final_price || '');
      setLoading(false);
    };
    fetchOrder();
  }, [orderId, orgId, navigate]);

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const updates = { status: newStatus };
      if (newStatus === 'quoted') updates.quoted_at = new Date().toISOString();
      if (newStatus === 'approved') updates.approved_at = new Date().toISOString();
      if (newStatus === 'delivered') updates.completed_at = new Date().toISOString();
      if (newStatus === 'paid') updates.paid_at = new Date().toISOString();

      const { error } = await supabase.from('service_orders').update(updates).eq('id', order.id);
      if (error) throw error;
      setOrder(prev => ({ ...prev, ...updates }));
    } catch (err) {
      console.error('Failed to update status:', err);
      alert('Failed to update status: ' + err.message);
    } finally {
      setUpdating(false);
    }
  };

  const saveNotes = async () => {
    try {
      const { error } = await supabase.from('service_orders')
        .update({ internal_notes: editNotes })
        .eq('id', order.id);
      if (error) throw error;
      setOrder(prev => ({ ...prev, internal_notes: editNotes }));
    } catch (err) {
      console.error('Failed to save notes:', err);
    }
  };

  const savePrice = async (field) => {
    const value = field === 'quoted_price' ? editQuotedPrice : editFinalPrice;
    try {
      const { error } = await supabase.from('service_orders')
        .update({ [field]: parseFloat(value) })
        .eq('id', order.id);
      if (error) throw error;
      setOrder(prev => ({ ...prev, [field]: parseFloat(value) }));
    } catch (err) {
      console.error('Failed to save price:', err);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
        Loading order...
      </div>
    );
  }

  if (!order) return null;

  const cfg = STATUS_CONFIG[order.status] || { label: order.status, color: '#666', bg: '#f3f4f6' };

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button onClick={() => navigate('/curator/service-orders')}
          style={{ ...btnStyle, padding: '6px 12px' }}>
          <ArrowLeft size={16} /> Back
        </button>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 600 }}>{order.order_num}</h2>
          <div style={{ fontSize: '.82rem', color: '#999', marginTop: 2 }}>
            {new Date(order.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
        <span style={{
          padding: '6px 16px', borderRadius: 20, fontSize: '.82rem', fontWeight: 600,
          color: cfg.color, background: cfg.bg,
        }}>{cfg.label}</span>
      </div>

      {/* Status workflow */}
      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <label style={labelStyle}>Status</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {Object.entries(STATUS_CONFIG).map(([key, c]) => (
            <button key={key} onClick={() => updateStatus(key)} disabled={updating}
              style={{
                padding: '7px 14px', borderRadius: 6, fontSize: '.75rem', fontWeight: 500,
                background: order.status === key ? c.bg : '#f9fafb',
                color: order.status === key ? c.color : '#aaa',
                border: `1px solid ${order.status === key ? c.color : '#e5e7eb'}`,
                cursor: updating ? 'wait' : 'pointer', transition: 'all .15s',
              }}>{c.label}</button>
          ))}
        </div>
      </div>

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Left: Customer */}
        <div style={cardStyle}>
          <label style={labelStyle}>Customer</label>
          <div style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 6 }}>{order.customer_name}</div>
          <div style={{ fontSize: '.85rem', color: '#555', lineHeight: 1.8 }}>
            <div><Mail size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
              <a href={`mailto:${order.customer_email}`} style={{ color: '#4f46e5' }}>{order.customer_email}</a>
            </div>
            {order.customer_phone && (
              <div><Phone size={13} style={{ verticalAlign: -2, marginRight: 6 }} />{order.customer_phone}</div>
            )}
          </div>
          {order.discount_percent > 0 && (
            <div style={{ marginTop: 10, padding: '8px 12px', background: '#fffbeb', borderRadius: 6, fontSize: '.8rem', color: '#92400e' }}>
              ★ {order.discount_reason} — {order.discount_percent}% artist discount
            </div>
          )}
        </div>

        {/* Right: Service details */}
        <div style={cardStyle}>
          <label style={labelStyle}>Service Details</label>
          <div style={{ fontSize: '1rem', marginBottom: 8 }}>
            {SERVICE_ICONS[order.service_type]} {SERVICE_LABELS[order.service_type]}
            {order.tier && <span style={{ marginLeft: 8, fontSize: '.82rem', color: '#666' }}>({order.tier})</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: '.85rem', color: '#555' }}>
            <div>
              <span style={miniLabel}>Size</span>
              {order.size_width && order.size_height ? `${order.size_width} × ${order.size_height}″` : 'Not specified'}
            </div>
            <div>
              <span style={miniLabel}>Quantity</span>
              {order.quantity}
            </div>
            <div>
              <span style={miniLabel}>Paper</span>
              {order.paper_type || 'Not specified'}
            </div>
            <div>
              <span style={miniLabel}>Deadline</span>
              {order.deadline ? new Date(order.deadline).toLocaleDateString() : 'None'}
            </div>
          </div>
        </div>
      </div>

      {/* Customer notes */}
      {order.notes && (
        <div style={{ ...cardStyle, marginTop: 20 }}>
          <label style={labelStyle}>Customer Notes</label>
          <div style={{ fontSize: '.88rem', color: '#333', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {order.notes}
          </div>
        </div>
      )}

      {/* Pricing */}
      <div style={{ ...cardStyle, marginTop: 20 }}>
        <label style={labelStyle}>Pricing</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div>
            <span style={miniLabel}>Quoted Price</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: '.9rem', color: '#666' }}>$</span>
              <input type="number" step="0.01" value={editQuotedPrice}
                onChange={e => setEditQuotedPrice(e.target.value)}
                style={{ ...inputStyle, width: 140 }} placeholder="0.00" />
              <button onClick={() => savePrice('quoted_price')} style={btnSmall}>Save</button>
            </div>
          </div>
          <div>
            <span style={miniLabel}>Final Price</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: '.9rem', color: '#666' }}>$</span>
              <input type="number" step="0.01" value={editFinalPrice}
                onChange={e => setEditFinalPrice(e.target.value)}
                style={{ ...inputStyle, width: 140 }} placeholder="0.00" />
              <button onClick={() => savePrice('final_price')} style={btnSmall}>Save</button>
            </div>
          </div>
        </div>
      </div>

      {/* Internal notes */}
      <div style={{ ...cardStyle, marginTop: 20 }}>
        <label style={labelStyle}>Internal Notes (staff only)</label>
        <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)}
          style={{ ...inputStyle, width: '100%', minHeight: 100, resize: 'vertical', boxSizing: 'border-box' }}
          placeholder="Production notes, colour matching info, etc..." />
        <button onClick={saveNotes} style={{ ...btnSmall, marginTop: 8 }}>Save Notes</button>
      </div>

      {/* Timestamps */}
      <div style={{ ...cardStyle, marginTop: 20, marginBottom: 40 }}>
        <label style={labelStyle}>Timeline</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, fontSize: '.82rem', color: '#666' }}>
          <div><span style={miniLabel}>Created</span>{new Date(order.created_at).toLocaleString()}</div>
          {order.quoted_at && <div><span style={miniLabel}>Quoted</span>{new Date(order.quoted_at).toLocaleString()}</div>}
          {order.approved_at && <div><span style={miniLabel}>Approved</span>{new Date(order.approved_at).toLocaleString()}</div>}
          {order.paid_at && <div><span style={miniLabel}>Paid</span>{new Date(order.paid_at).toLocaleString()}</div>}
          {order.completed_at && <div><span style={miniLabel}>Delivered</span>{new Date(order.completed_at).toLocaleString()}</div>}
        </div>
      </div>
    </div>
  );
};

const cardStyle = {
  background: '#fff', borderRadius: 10, padding: 20,
  border: '1px solid #f0f0f0',
};

const btnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', borderRadius: 6, fontSize: '.82rem',
  background: '#fff', border: '1px solid #e5e7eb', color: '#333',
  cursor: 'pointer', transition: 'all .15s',
};

const btnSmall = {
  padding: '6px 12px', borderRadius: 4, fontSize: '.75rem',
  background: '#f9fafb', border: '1px solid #e5e7eb', color: '#333',
  cursor: 'pointer',
};

const inputStyle = {
  padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb',
  fontSize: '.85rem', outline: 'none',
};

const labelStyle = {
  display: 'block', fontSize: '.72rem', color: '#999',
  textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8,
};

const miniLabel = {
  display: 'block', fontSize: '.7rem', color: '#aaa',
  textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 2,
};

export default CuratorServiceOrderDetail;
